import DocumentSchema from '@components/DocumentSchema';
import type { Table } from 'schema-dts';
import Tooltip from '@/components/Tooltip';
import {
  TooltipName,
  TipContainer,
  AnalystDataTable,
  TableRow,
  TextColor,
  TableWrapper,
} from './styled';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import type { PriceData } from '../../queryPriceData';
import type { PriceTargetData } from '../../queryPriceTargets';
import { format } from 'date-fns/esm';
import { formatterCurrency, formatterPercentage } from '@/utilities/format';
import {
  getValidPrice,
  getPriceGrowthData,
  getCurrentPrice,
  getGrowthColor,
} from '../../utils';
import { subYears } from 'date-fns';
import { Grid, useTheme } from '@simplywallst/ui-core';
import { selector } from '@/pages/CompanyReport/components/Valuation';
import { shallowEqual, useSelector } from 'react-redux';
import { daysBack, dispersionThreshold } from '../../constants';

interface Props {
  tickerSymbol: string;
  currencyISO: string;
  priceData: PriceData['priceData'];
  priceTargetData: PriceTargetData['priceTargetData'];
  analystData: PriceTargetData['analystData'];
}
export const AnalystPriceTargetTable = ({
  tickerSymbol,
  currencyISO,
  priceData,
  priceTargetData,
  analystData,
}: Props) => {
  const theme = useTheme();
  const getTerm = useGlossaryTerms();
  const valuationData = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);
  const heading = `${tickerSymbol} Analyst Price Target`;
  const caption = `The above table shows the analyst ${tickerSymbol} forecast and predictions for the stock price in 12 month’s time.`;
  const reversePriceTarget = priceTargetData.slice().reverse();
  return (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: heading,
          about: caption,
        }}
      />
      <TableWrapper data-cy-id="analyst-price-target-table">
        <AnalystDataTable data-cy-id="analyst-price-target-table">
          <caption>{caption}</caption>
          <thead>
            <tr>
              <th>Date</th>
              <th>Share Price</th>
              <th>
                <Tooltip
                  openDelay={300}
                  light
                  title={
                    <TipContainer>
                      {getTerm('Average 1Y Price Target')}
                    </TipContainer>
                  }
                >
                  <TooltipName>{'Average 1Y Price Target'}</TooltipName>
                </Tooltip>
              </th>
              <th>
                <Tooltip
                  openDelay={300}
                  light
                  title={<TipContainer>{getTerm('Dispersion')}</TipContainer>}
                >
                  <TooltipName>{'Dispersion'}</TooltipName>
                </Tooltip>
              </th>
              <th>High</th>
              <th>Low</th>
              <th>
                <Tooltip
                  openDelay={300}
                  light
                  title={
                    <TipContainer>{getTerm('1Y Actual Price')}</TipContainer>
                  }
                >
                  <TooltipName>{'1Y Actual price'}</TooltipName>
                </Tooltip>
              </th>
              <th>Analysts</th>
            </tr>
          </thead>
          <tbody>
            {reversePriceTarget.map((priceTarget, i) => {
              const firstRow = i === 0;
              const date = priceTarget[0];
              const price = priceTarget[1];
              const oneYearAgo = subYears(date, 1).getTime();
              const validPrice = getValidPrice(oneYearAgo, priceData, daysBack);
              const actualPrice = getCurrentPrice(date, priceData);
              const { growth, growthSign } = getPriceGrowthData(
                validPrice,
                price,
                valuationData.shouldShowAptCalc
              );
              const growthColor = getGrowthColor(growth, theme);
              return (
                <TableRow
                  selected={firstRow}
                  key={i}
                  data-cy-id={`analyst-table-row-${i}`}
                >
                  <td>{firstRow ? 'Current' : format(date, 'MMM ’yy')}</td>
                  <td>{formatterCurrency(validPrice, currencyISO)}</td>
                  <td>
                    <Grid gap="10px" templateColumns="auto 55px">
                      <Grid>{formatterCurrency(price, currencyISO)}</Grid>
                      <Grid>
                        <TextColor color={growthColor}>
                          {growthSign + formatterPercentage(growth)}
                        </TextColor>
                      </Grid>
                    </Grid>
                  </td>
                  <td>
                    <TextColor
                      color={
                        analystData[date].dispersion < dispersionThreshold
                          ? theme.color.good
                          : theme.color.bad
                      }
                    >
                      {formatterPercentage(analystData[date].dispersion)}
                    </TextColor>
                  </td>
                  <td>
                    {formatterCurrency(
                      analystData[date].priceTargetMax,
                      currencyISO
                    )}
                  </td>
                  <td>
                    {formatterCurrency(
                      analystData[date].priceTargetMin,
                      currencyISO
                    )}
                  </td>
                  <td>{formatterCurrency(actualPrice, currencyISO)}</td>
                  <td>{analystData[date].analystCount}</td>
                </TableRow>
              );
            })}
          </tbody>
        </AnalystDataTable>
      </TableWrapper>
    </>
  );
};
